/**
 * KAN-632: Telegram-username клиента ссылкой на t.me и блок "Контакты ЛПР".
 * KAN-889: ручная правка контактов ЛПР (форма в LeadContactsForm).
 */
import { useState } from 'react';
import { ExternalLink, Pencil, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LeadContactsForm } from './LeadContactsForm';
import type { LeadContact } from '@/types/client';

/** @username ссылкой на t.me; пустой username - ничего. */
export function TelegramUsernameLink({
  username,
  className = '',
}: {
  username?: string | null;
  className?: string;
}) {
  if (!username) return null;
  const name = username.replace(/^@/, '');
  return (
    <a
      href={`https://t.me/${name}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={`text-sm text-blue-600 hover:underline ${className}`}
      data-testid="client-telegram-username"
    >
      @{name}
    </a>
  );
}

/** KAN-806: иконка-ссылка "Открыть в Telegram"; без ссылки ничего. */
export function TelegramIconLink({
  href,
  className = '',
}: {
  href?: string | null;
  className?: string;
}) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Открыть в Telegram"
      title="Открыть в Telegram"
      className={className}
    >
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-2 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="text-right break-all">{children}</span>
    </div>
  );
}

interface LeadContactsCardProps {
  contacts?: LeadContact[];
  /** KAN-889: client id for the edit request; required with canEdit. */
  clientId?: string;
  /** KAN-889: PATCH of the client card is open: "Изменить" / "Добавить ЛПР". */
  canEdit?: boolean;
}

/**
 * Карточка "Контакты ЛПР" из ``lead_contacts``. Без контактов: при праве
 * правки кнопка "Добавить ЛПР", иначе ничего. KAN-889: "Изменить" открывает
 * форму на месте списка.
 */
export function LeadContactsCard({ contacts, clientId, canEdit = false }: LeadContactsCardProps) {
  const [editing, setEditing] = useState(false);
  const editable = canEdit && !!clientId;
  const hasContacts = !!contacts && contacts.length > 0;
  if (!hasContacts && !editable) return null;

  if (!hasContacts && !editing) {
    return (
      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={() => setEditing(true)}
        data-testid="lead-contacts-add"
      >
        <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />
        Добавить ЛПР
      </Button>
    );
  }

  return (
    <Card data-testid="client-lead-contacts">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">Контакты ЛПР</CardTitle>
        {editable && hasContacts && !editing && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid="lead-contacts-edit">
            <Pencil className="mr-1 h-4 w-4" aria-hidden="true" />
            Изменить
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {editing && clientId ? (
          <LeadContactsForm clientId={clientId} contact={contacts?.[0]} onClose={() => setEditing(false)} />
        ) : (
          contacts?.map((c, i) => <LeadContactRows key={i} contact={c} />)
        )}
      </CardContent>
    </Card>
  );
}

function LeadContactRows({ contact: c }: { contact: LeadContact }) {
  return (
    <div className="space-y-2">
      {c.name && <Row label="Имя">{c.name}</Row>}
      {c.role && <Row label="Роль">{c.role}</Row>}
      {c.telegram && (
        <Row label="Telegram">
          <TelegramUsernameLink username={c.telegram} />
        </Row>
      )}
      {c.phone && (
        <Row label="Телефон">
          <a href={`tel:${c.phone}`} className="text-blue-600 hover:underline">
            {c.phone}
          </a>
        </Row>
      )}
      {c.email && (
        <Row label="Email">
          <a href={`mailto:${c.email}`} className="text-blue-600 hover:underline">
            {c.email}
          </a>
        </Row>
      )}
    </div>
  );
}
